import { api } from './config';
import { AxiosResponse } from 'axios';
import type { NdaTemplate } from '@/components/contracts/ndaTemplate';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  const result: Record<string, any> = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = toCamelCase(obj[key]);
  }
  return result;
};

// Helper function to convert camelCase to snake_case for request body
const toSnakeCase = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  const result: Record<string, any> = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    result[snakeKey] = toSnakeCase(obj[key]);
  }
  return result;
};

// Transform API response data from snake_case to camelCase
const transformResponse = <T>(response: AxiosResponse<any>): AxiosResponse<T> => {
  if (response.data) {
    response.data = toCamelCase(response.data);
  }
  return response as AxiosResponse<T>;
};

// 계약 요청 페이로드 구성 — NDA 조항(templateData)은 snake 변환에서 격리.
// 조항 키(title/content/bullets/sections...)를 정본과 동일하게 보존해 비교/렌더 정합 유지.
const buildContractPayload = (data: ContractCreateRequest): Record<string, unknown> => {
  const { templateData, ...rest } = data;
  const payload = toSnakeCase(rest) as Record<string, unknown>;
  if (templateData) {
    payload.template_data = templateData;
  }
  return payload;
};

// Types
export interface ContractCreateRequest {
  contractType: string;
  brandCompanyName: string;
  brandAddress: string;
  brandPlace?: string;
  brandSignerName: string;
  brandSignerFunction: string;
  signatureId?: number;
  signatureData?: string;
  // 브랜드가 수정한 NDA 조항 (미수정 시 생략 → 백엔드가 정본 default 시드)
  templateData?: NdaTemplate;
}

export interface ContractSignRequest {
  signerName: string;
  signerFunction: string;
  signatureId?: number;
  signatureData?: string;
}

export interface ContractRequestDto {
  brandId: number;
  contractType?: string;
  message?: string;
}

export interface ContractUpdateDto {
  notes?: string;
  status?: string;
  brandCompanyName?: string;
  brandAddress?: string;
  brandPlace?: string;
  brandSignerName?: string;
  brandSignerFunction?: string;
}

export interface ContractResponse {
  id: number;
  brandId: number;
  contractType: string;
  contractCode: string;
  status: 'draft' | 'pending_brand' | 'pending_admin' | 'completed' | 'expired' | 'cancelled';
  brandCompanyName: string;
  brandAddress: string;
  brandPlace?: string;
  brandSignerName: string;
  brandSignerFunction: string;
  brandSignedAt?: string;
  brandSignatureData?: string;
  brandSigned?: boolean;
  npSignerId?: number;
  npSignerName?: string;
  npSignerFunction?: string;
  npSignedAt?: string;
  npSignatureData?: string;
  adminSigned?: boolean;
  pdfUrl?: string;
  pdfGeneratedAt?: string;
  // 조항 데이터 (화면/PDF 공통 렌더 소스). null이면 정본 default 폴백
  templateData?: NdaTemplate | null;
  // 정본 대비 변경된 clause id 목록 (admin highlight 구동)
  modifiedSections?: string[];
  notes?: string;
  expiresAt?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureResponse {
  id: number;
  userType: 'brand' | 'admin';
  userId: number;
  signatureName: string;
  signatureData: string;
  isDefault: boolean;
  createdAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// Contract API
export const contractsApi = {
  // NDA 정본(default) 조항 템플릿 조회 (단일 소스 원천)
  getDefaultTemplate: async (): Promise<AxiosResponse<ApiResponse<NdaTemplate>>> => {
    const response = await api.get('/contracts/nda-template/default');
    return transformResponse(response);
  },

  // Brand: Create and sign contract
  createContract: async (data: ContractCreateRequest): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.post('/contracts', buildContractPayload(data));
    return transformResponse(response);
  },

  // Get my contracts (Brand)
  getMyContracts: async (): Promise<AxiosResponse<ApiResponse<ContractResponse[]>>> => {
    const response = await api.get('/contracts/my');
    return transformResponse(response);
  },

  // Get contract by ID
  getContract: async (id: number): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.get(`/contracts/${id}`);
    return transformResponse(response);
  },

  // Cancel contract (Brand)
  cancelContract: async (id: number, reason?: string): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.post(`/contracts/${id}/cancel`, { reason });
    return transformResponse(response);
  },

  // Brand: Get pending contracts (waiting for brand signature)
  getPendingBrandContracts: async (): Promise<AxiosResponse<ApiResponse<ContractResponse[]>>> => {
    const response = await api.get('/contracts/pending-brand');
    return transformResponse(response);
  },

  // Brand: Sign requested contract
  signBrandContract: async (id: number, data: ContractCreateRequest): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.put(`/contracts/${id}/sign-brand`, buildContractPayload(data));
    return transformResponse(response);
  },

  // Admin: Request contract from Brand
  // Note: Don't use toSnakeCase - backend DTO expects camelCase via @JsonProperty
  requestContract: async (data: ContractRequestDto): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.post('/contracts/request', data);
    return transformResponse(response);
  },

  // Admin: Update contract (notes, status)
  updateContract: async (id: number, data: ContractUpdateDto): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.put(`/contracts/${id}`, toSnakeCase(data));
    return transformResponse(response);
  },

  // Admin: Cancel contract
  cancelContractByAdmin: async (id: number, reason?: string): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.post(`/contracts/${id}/cancel-admin`, { reason });
    return transformResponse(response);
  },

  // Admin: Sign contract
  signContract: async (id: number, data: ContractSignRequest): Promise<AxiosResponse<ApiResponse<ContractResponse>>> => {
    const response = await api.post(`/contracts/${id}/sign`, toSnakeCase(data));
    return transformResponse(response);
  },

  // Admin: Get pending contracts
  getPendingContracts: async (): Promise<AxiosResponse<ApiResponse<ContractResponse[]>>> => {
    const response = await api.get('/contracts/pending');
    return transformResponse(response);
  },

  // Admin: Get contracts by status
  getContractsByStatus: async (
    status: string,
    page = 0,
    size = 20
  ): Promise<AxiosResponse<ApiResponse<{ content: ContractResponse[]; totalElements: number; totalPages: number }>>> => {
    const response = await api.get(`/contracts/status/${status}`, { params: { page, size } });
    return transformResponse(response);
  },

  // Signature Management
  saveSignature: async (
    signatureName: string,
    signatureData: string,
    setAsDefault = false
  ): Promise<AxiosResponse<ApiResponse<SignatureResponse>>> => {
    const response = await api.post('/contracts/signatures', toSnakeCase({ signatureName, signatureData, setAsDefault }));
    return transformResponse(response);
  },

  getMySignatures: async (): Promise<AxiosResponse<ApiResponse<SignatureResponse[]>>> => {
    const response = await api.get('/contracts/signatures/my');
    return transformResponse(response);
  },

  getDefaultSignature: async (): Promise<AxiosResponse<ApiResponse<SignatureResponse>>> => {
    const response = await api.get('/contracts/signatures/default');
    return transformResponse(response);
  },

  deleteSignature: (id: number): Promise<AxiosResponse<ApiResponse<void>>> => {
    return api.delete(`/contracts/signatures/${id}`);
  },

  renameSignature: async (id: number, signatureName: string): Promise<AxiosResponse<ApiResponse<SignatureResponse>>> => {
    const response = await api.patch(`/contracts/signatures/${id}`, toSnakeCase({ signatureName }));
    return transformResponse(response);
  },

  // PDF 서명/도장 업로드 → 첫 페이지 PNG data URL 변환 (이미지 업로드는 프론트에서 직접 data URL 처리)
  convertSignatureFile: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/contracts/signatures/convert-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const transformed = transformResponse<ApiResponse<{ dataUrl: string }>>(response);
    return transformed.data.data.dataUrl;
  },

  // PDF
  downloadPdf: (id: number): Promise<AxiosResponse<Blob>> => {
    return api.get(`/contracts/${id}/pdf`, { responseType: 'blob' });
  },

  previewContract: async (id: number): Promise<AxiosResponse<ApiResponse<string>>> => {
    const response = await api.get(`/contracts/${id}/preview`);
    return transformResponse(response);
  },

  generatePdf: async (id: number): Promise<AxiosResponse<ApiResponse<string>>> => {
    const response = await api.post(`/contracts/${id}/generate-pdf`);
    return transformResponse(response);
  },
};

export default contractsApi;
